import { Box, FormHelperText, IconButton, InputAdornment, SxProps, TextField } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export const INVALID_DATE = 'Invalid date';

type InternalDatePickerProps = {
    min: string;
    max: string;
    dateValue: string;
    onDateChange: (date: string) => void;
    sx?: SxProps;
    variant?: 'standard' | 'outlined';
    disabled?: boolean;
    intervalError?: boolean;
};

const InternalDatePicker = ({
    dateValue,
    min,
    max,
    intervalError,
    disabled,
    onDateChange,
    sx,
    variant
}: InternalDatePickerProps) => {
    //Mobile Icon Open/Close
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    //endregion

    useEffect(() => {
        setError(
            // Check for invalid date
            dateValue.toString() === INVALID_DATE ||
                // Check whether dateValue is parsable into date object
                isNaN(Date.parse(dateValue)) ||
                // Check that year is greater than or equal to 1900
                new Date(dateValue).getFullYear() < 1900
        );
    }, [dateValue, error]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en'}>
            <Box sx={{ display: 'block' }}>
                <DatePicker
                    disabled={disabled}
                    open={open}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={(date) => {
                        if (date === null) {
                            setError(true);
                            return;
                        }
                        if (setError) setError(false);
                        onDateChange(date);
                    }}
                    value={dateValue}
                    minDate={min}
                    maxDate={max}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <IconButton edge="end" onClick={handleOpen}>
                                    <EventIcon color="secondary" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    renderInput={(props) => (
                        <TextField
                            required
                            margin="none"
                            onChange={(e) => {
                                if (e.target.value === '') {
                                    onDateChange(INVALID_DATE);
                                }
                            }}
                            helperText={
                                error && !intervalError ? 'Please select a valid date.' : ''
                            }
                            variant={variant ? variant : 'outlined'}
                            InputLabelProps={{
                                style: { fontSize: '0.875rem', fontWeight: 'bold' }
                            }}
                            fullWidth
                            sx={
                                sx
                                    ? sx
                                    : {
                                          '& .MuiInputBase-root': {
                                              height: '2.5rem',
                                              textAlign: 'center'
                                          }
                                      }
                            }
                            {...props}
                        />
                    )}
                />

                {intervalError ? (
                    <FormHelperText error>Please select a valid interval.</FormHelperText>
                ) : null}
            </Box>
        </LocalizationProvider>
    );
};

export default InternalDatePicker;
